import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useStudyStore } from './study';
import type { SidebarLinkModel } from '@/components/sidebar/interfaces/SidebarLinkModel';
import { SidebarLinkIdentifiers } from '@/components/sidebar/LinkIdentifiers';
import { routeConstants } from '@/utils';

export const useDashboardStore = defineStore('dashboard', () => {
  const studyStore = useStudyStore();

  const selectedMenu = ref<number>(0);
  const sidebarLinks = ref<SidebarLinkModel[]>([]);
  const userOptionLinks = ref<SidebarLinkModel[]>([]);

  const setMenu = (id: number) => {
    selectedMenu.value = id;
  };

  const setDashboardLinks = () => {
    const permissions = studyStore.studyPermissions;

    const links: SidebarLinkModel[] = [];

    const dataNestedLinks: SidebarLinkModel[] = [];

    if (permissions?.manageInvest && selectedMenu.value === SidebarLinkIdentifiers.DATA) {
      dataNestedLinks.push({
        id: SidebarLinkIdentifiers.NESTED_NEW_VIEW,
        label: 'New View',
        icon: 'plus-circle',
        iconStyle: 'fal',
        selected: false,
        route: '',
        routeEnabled: false
      });
    }

    links.push({
      id: SidebarLinkIdentifiers.DATA,
      label: 'Data',
      icon: 'database',
      iconStyle: 'fal',
      selected: false,
      route: routeConstants.DATA.name,
      routeEnabled: false,
      nestedLinks: dataNestedLinks
    });

    if (permissions?.manageInvest) {
      links.push({
        id: SidebarLinkIdentifiers.FORMS,
        label: 'Forms',
        icon: 'layer-group',
        iconStyle: 'fal',
        selected: false,
        route: routeConstants.FORMS.name,
        routeEnabled: true
      });

      links.push({
        id: SidebarLinkIdentifiers.ANALYTICS,
        label: 'Analytics',
        icon: 'chart-bar',
        iconStyle: 'fal',
        selected: false,
        route: routeConstants.ANALYTICS.name,
        routeEnabled: true
      });
    }

    if (studyStore.selectedStudy?.flags?.customDataDictionary === 'PUMP') {
      links.push({
        id: SidebarLinkIdentifiers.SURGEON_DASHBOARD,
        label: 'Surgeon Dashboard',
        icon: 'monitor-waveform',
        iconStyle: 'far',
        selected: false,
        route: routeConstants.DEFAULT_SURGEON_DASHBOARD.name,
        routeEnabled: true
      });

      if (studyStore.selectedStudy?.flags?.pumpQcReports?.enable) {
        links.push({
          id: SidebarLinkIdentifiers.PUMP_QC_REPORT,
          label: 'PUMP Reports',
          icon: 'file',
          iconStyle: 'fal',
          selected: false,
          route: routeConstants.PUMP_QC_REPORT.name,
          routeEnabled: true
        });
      }
    }

    if (permissions?.export) {
      links.push({
        id: SidebarLinkIdentifiers.EXPORTS,
        label: 'Data Exports',
        icon: 'download',
        iconStyle: 'fal',
        selected: false,
        route: routeConstants.EXPORT.name,
        routeEnabled: true
      });
    }

    if (permissions?.manageInvest) {
      links.push({
        id: SidebarLinkIdentifiers.ACTIVITY,
        label: 'Activity',
        icon: 'list',
        iconStyle: 'fal',
        selected: false,
        route: routeConstants.ACTIVITY.name,
        routeEnabled: true
      });

      links.push({
        id: SidebarLinkIdentifiers.MANAGE_USERS,
        label: 'Users',
        icon: 'users',
        iconStyle: 'fal',
        selected: false,
        route: routeConstants.MANAGE.name,
        routeEnabled: false
      });

      links.push({
        id: SidebarLinkIdentifiers.STUDY_SETTINGS,
        label: 'Study Settings',
        icon: 'cog',
        iconStyle: 'fal',
        selected: false,
        route: routeConstants.STUDY_SETTINGS.name,
        routeEnabled: true
      });
    }

    sidebarLinks.value = links;

    setUserOptionLinks();
  };

  const setSelectedDashboardLink = (id: number) => {
    sidebarLinks.value.forEach(link => link.selected = link.id === id);
  };

  const setUserOptionLinks = () => {
    const organizationIdentifier = studyStore.selectedStudy?.organizationIdentifier;
    const permissions = studyStore.studyPermissions;

    const userLinks: SidebarLinkModel[] = [];

    userLinks.push({
      id: SidebarLinkIdentifiers.ACCOUNT_SETTINGS,
      label: 'Account settings',
      icon: 'circle-user',
      iconStyle: 'fas',
      selected: false,
      route: routeConstants.ACCOUNT_SETTINGS.name,
      routeEnabled: true
    });

    if (permissions?.manageOrganization) {
      userLinks.push({
        id: SidebarLinkIdentifiers.BILLING_SETTINGS,
        label: 'Billing settings',
        icon: 'receipt',
        iconStyle: 'fas',
        selected: false,
        route: `organizations/${organizationIdentifier}/billing`,
        routeEnabled: false
      });
    }

    userLinks.push({
      id: SidebarLinkIdentifiers.LOG_OUT,
      label: 'Log out',
      icon: 'sign-out',
      iconStyle: 'fas',
      selected: false,
      route: 'logout',
      routeEnabled: true
    });

    userOptionLinks.value = userLinks;
  };

  return {
    selectedMenu,
    sidebarLinks,
    userOptionLinks,
    setMenu,
    setDashboardLinks,
    setUserOptionLinks,
    setSelectedDashboardLink
  };
});
